<template>
	<div
		class="d-flex pt-2 illustrated-data-tile"
		:style="tileGlobalStyles"
	>
		<div
			class="d-flex flex-column align-center w-100 ap-white illustrated-data-tile__wrapper"
			:style="tileStyles"
		>
			<AmeliproIcon
				class="mx-auto illustrated-data-tile__icon"
				:icon="iconName"
				:icon-color="tileInfoColor"
				size="60px"
			/>

			<p
				class="mb-0 text-center illustrated-data-tile__label"
				:heading-level="titleLevel"
				role="heading"
			>
				<span class="d-block text-center illustrated-data-tile__label__line-1">
					{{ labelFirstLine }}
				</span>

				<span class="d-block text-center text-uppercase illustrated-data-tile__label__line-2">
					{{ labelSecondLine }}
				</span>
			</p>

			<p class="text-center illustrated-data-tile__info">
				<span
					v-for="(info, index) in complementaryInformation"
					:key="index"
					class="d-block"
				>
					{{ info.label }}

					<span class="font-weight-semibold">
						{{ info.value ? info.value : undefined }}
					</span>
				</span>
			</p>
		</div>
	</div>
</template>

<script setup lang="ts">
	import AmeliproIcon from '../AmeliproIcon';
	import { AmeliproIllustratedDataInfoTypes } from './types';
	import { IndexedObject } from '../../types';
	import { PropType } from 'vue/types/v3-component-props';
	import { computed } from 'vue';
	import { convertToHex } from '../../functions/convertToHex';

	const props = defineProps({
		complementaryInformation: {
			type: Array as PropType<AmeliproIllustratedDataInfoTypes[]>,
			default: () => [],
		},
		iconName: {
			type: String,
			required: true,
		},
		labelFirstLine: {
			type: String,
			required: true,
		},
		labelSecondLine: {
			type: String,
			required: true,
		},
		tileInfoColor: {
			type: String,
			default: 'ap-grey darken-1',
		},
		tileMinHeight: {
			type: String,
			default: undefined,
		},
		tilePadding: {
			type: String,
			default: '2rem 1rem',
		},
		tileWidth: {
			type: String,
			default: '100%',
		},
		titleLevel: {
			type: Number,
			default: 2,
		},
	});

	const tileGlobalStyles = computed<IndexedObject>(() => {
		const styles: IndexedObject = { width: props.tileWidth };

		if (props.tileMinHeight !== undefined) {
			styles.minHeight = props.tileMinHeight;
		}

		return styles;
	});

	const tileStyles = computed<IndexedObject>(() => {
		const styles: IndexedObject = { border: `2px solid ${convertToHex('ap-grey lighten-3')} !important` };

		if (props.tilePadding !== undefined) {
			styles.padding = props.tilePadding;
		}

		return styles;
	});
</script>

<style lang="scss" scoped>
	.illustrated-data-tile__wrapper {
		border-radius: 0.5rem;
		transform: translateY(0);
		transition: transform 0.1s ease-in-out;

		&:hover {
			transform: translateY(-8px);
		}
	}

	.illustrated-data-tile__label__line-1 {
		margin-top: 2rem;
		margin-bottom: 1rem;
		font-size: $font-size-sm;
		font-weight: $ap-font-weight-semi-bold;
	}

	.illustrated-data-tile__label__line-2 {
		margin-bottom: 2rem;
		font-size: $font-size-xs;
		font-weight: $ap-font-weight-regular;
	}
</style>
